/**
 * Northern Shopper — product card quantity controls (+ / input / -)
 */
(function () {
	'use strict';

	var NS = (window.NorthernShopper = window.NorthernShopper || {});
	var STORAGE_KEY = 'northernShopperCartEvent';
	// How long to keep collecting +/- taps before hitting the cart. 300ms matches
	// the theme's own cart debounce (assets/cart.js CartItems, quick-order-list).
	var CLICK_DEBOUNCE_MS = 300;
	// Typing needs a longer window: this listens to `input` rather than the
	// theme's `change`, so 300ms would commit part-way through a multi-digit
	// number. The theme only commits a typed value on blur/Enter.
	var TYPING_DEBOUNCE_MS = 600;
	var cartByVariant = {};
	// Quantities the shopper has asked for but that the cart has not confirmed.
	// Keyed by variant id; these win over cart state when rendering a card.
	var pendingQty = {};
	var pendingTimers = {};
	var inflight = {};
	var broadcastTimer = null;
	var lastSnapshot = { ts: 0, item_count: 0, items: [], trigger: null };
	var suppressBroadcast = false;
	var suppressBroadcastTimer = null;

	function slimLine(item) {
		var variantId = item.variant_id != null ? item.variant_id : item.id;
		return {
			variant_id: variantId,
			quantity: item.quantity,
			key: item.key,
			sku: item.sku || '',
			title: NS.displayProductTitle(item.product_title || item.title || ''),
		};
	}

	function qtyByVariant(items) {
		var map = {};
		(items || []).forEach(function (item) {
			if (item.variant_id == null) return;
			var id = String(item.variant_id);
			map[id] = (map[id] || 0) + (parseInt(item.quantity, 10) || 0);
		});
		return map;
	}

	function findLine(items, variantId) {
		var id = String(variantId);
		for (var i = 0; i < (items || []).length; i++) {
			if (String(items[i].variant_id) === id) return items[i];
		}
		return null;
	}

	function diffTrigger(prevItems, nextItems) {
		var prev = qtyByVariant(prevItems);
		var next = qtyByVariant(nextItems);
		var ids = {};
		Object.keys(prev).forEach(function (id) {
			ids[id] = true;
		});
		Object.keys(next).forEach(function (id) {
			ids[id] = true;
		});

		var best = null;
		var bestAbs = 0;
		Object.keys(ids).forEach(function (id) {
			var before = prev[id] || 0;
			var after = next[id] || 0;
			var delta = after - before;
			if (!delta) return;
			var abs = Math.abs(delta);
			if (abs <= bestAbs) return;
			bestAbs = abs;
			var line = findLine(nextItems, id) || findLine(prevItems, id);
			best = {
				action: delta > 0 ? 'added' : 'removed',
				variant_id: parseInt(id, 10),
				quantity: abs,
				sku: (line && line.sku) || '',
				title: NS.displayProductTitle((line && (line.product_title || line.title)) || ''),
			};
		});
		return best;
	}

	function buildSnapshot(cart, trigger) {
		var items = ((cart && cart.items) || []).map(slimLine);
		var itemCount =
			cart && typeof cart.item_count === 'number'
				? cart.item_count
				: items.reduce(function (sum, line) {
					return sum + (parseInt(line.quantity, 10) || 0);
				}, 0);
		return {
			ts: Date.now(),
			item_count: itemCount,
			items: items,
			trigger: trigger || null,
		};
	}

	function rememberFromCart(cart, computeTrigger) {
		var trigger = computeTrigger ? diffTrigger(lastSnapshot.items, (cart && cart.items) || []) : null;
		lastSnapshot = buildSnapshot(cart, trigger);
	}

	function parseCartEvent(raw) {
		if (!raw) return null;
		try {
			var data = JSON.parse(raw);
			if (data && Array.isArray(data.items)) return data;
		} catch (e) {
			/* legacy timestamp-only values are not JSON objects */
		}
		return null;
	}

	function cartFingerprint(items, itemCount) {
		var lines = (items || []).map(function (item) {
			return String(item.variant_id || '') + ':' + String(item.key || '') + ':' + String(item.quantity || 0);
		});
		lines.sort();
		return String(itemCount || 0) + '#' + lines.join('|');
	}

	function beginSuppressBroadcast() {
		suppressBroadcast = true;
		clearTimeout(suppressBroadcastTimer);
		suppressBroadcastTimer = setTimeout(function () {
			suppressBroadcast = false;
		}, 4000);
	}

	function endSuppressBroadcast() {
		suppressBroadcast = false;
		clearTimeout(suppressBroadcastTimer);
	}

	function broadcastCartEvent(cart) {
		if (suppressBroadcast) return;
		if (isFullCart(cart)) {
			var prevFingerprint = cartFingerprint(lastSnapshot.items, lastSnapshot.item_count);
			rememberFromCart(cart, true);
			if (cartFingerprint(lastSnapshot.items, lastSnapshot.item_count) === prevFingerprint) {
				return;
			}
		} else {
			lastSnapshot = {
				ts: Date.now(),
				item_count: lastSnapshot.item_count,
				items: lastSnapshot.items.slice(),
				trigger: null,
			};
		}
		clearTimeout(broadcastTimer);
		broadcastTimer = setTimeout(function () {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(lastSnapshot));
			} catch (e) {
				/* ignore quota / private mode */
			}
		}, 50);
	}

	function isFullCart(cart) {
		return !!(cart && Array.isArray(cart.items));
	}

	function itemCountFromItems(items) {
		return (items || []).reduce(function (sum, line) {
			return sum + (parseInt(line.quantity, 10) || 0);
		}, 0);
	}

	/** Ensure publishers always carry item_count so <cart-count> can update without /cart.js. */
	function withItemCount(cart) {
		if (!cart || typeof cart !== 'object') return cart;
		if (typeof cart.item_count === 'number') return cart;
		var items = cart.items || [];
		return Object.assign({}, cart, { item_count: itemCountFromItems(items) });
	}

	function knownCartFromSnapshot() {
		return {
			items: lastSnapshot.items.slice(),
			item_count: lastSnapshot.item_count,
		};
	}

	function normalizeAddLines(payload) {
		if (!payload || typeof payload !== 'object') return [];
		if (Array.isArray(payload.items)) {
			return payload.items.map(slimLine).filter(function (line) {
				return line.variant_id != null && (parseInt(line.quantity, 10) || 0) > 0;
			});
		}
		if (payload.variant_id != null || (payload.id != null && payload.key != null)) {
			var line = slimLine(payload);
			return line.variant_id != null && (parseInt(line.quantity, 10) || 0) > 0 ? [line] : [];
		}
		return [];
	}

	/**
	 * `/cart/add.js` returns only the lines just added. Merge them into the cart
	 * we already know so the header count can update without a follow-up /cart.js.
	 */
	function mergeAddResponseIntoCart(cart, addPayload) {
		var added = normalizeAddLines(addPayload);
		if (!added.length) {
			throw new Error('Cart add returned no line items');
		}
		var items = ((cart && cart.items) || []).map(function (item) {
			return Object.assign({}, item);
		});
		added.forEach(function (line) {
			var index = -1;
			for (var i = 0; i < items.length; i++) {
				if (
					(line.key && items[i].key === line.key) ||
					String(items[i].variant_id) === String(line.variant_id)
				) {
					index = i;
					break;
				}
			}
			if (index >= 0) {
				items[index] = Object.assign({}, items[index], line, {
					key: line.key || items[index].key,
				});
			} else {
				items.push(line);
			}
		});
		return { items: items, item_count: itemCountFromItems(items) };
	}

	function applyLocalCartUpdate(cart) {
		if (isFullCart(cart)) {
			syncFromCart(withItemCount(cart), true);
			return;
		}
		fetchCartQuiet(true);
	}

	function getCartUrl(path) {
		var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
		if (!root.endsWith('/')) root += '/';
		return root + path.replace(/^\//, '');
	}

	/**
	 * `response.json()` throws "Unexpected end of JSON input" on an empty body
	 * (aborted crawler fetches, bot-blocked cart.js). Read as text first so that
	 * becomes a normal rejection callers can catch.
	 */
	function parseBody(text) {
		if (!text) return null;
		try {
			return JSON.parse(text);
		} catch (e) {
			return null;
		}
	}

	function errorText(payload) {
		if (!payload) return '';
		if (typeof payload === 'string') return payload;
		if (typeof payload.description === 'string' && payload.description) return payload.description;
		if (typeof payload.errors === 'string' && payload.errors) return payload.errors;
		if (payload.errors && typeof payload.errors === 'object') {
			return Object.keys(payload.errors)
				.map(function (key) {
					var value = payload.errors[key];
					return Array.isArray(value) ? value.filter(Boolean).join(' ') : value;
				})
				.filter(Boolean)
				.join(' ');
		}
		if (typeof payload.message === 'string' && payload.message && payload.message !== 'Cart Error') return payload.message;
		return '';
	}

	function isCartError(payload) {
		if (!payload || typeof payload !== 'object') return false;
		if (payload.errors) return true;
		return !!(payload.status && payload.status >= 400 && (payload.description || payload.message));
	}

	function readJson(response) {
		return response.text().then(function (text) {
			var payload = parseBody(text);
			if (!response.ok || isCartError(payload)) {
				var err = new Error(errorText(payload) || 'Could not add to cart');
				err.payload = payload;
				throw err;
			}
			if (!payload) throw new Error('Empty JSON response');
			return payload;
		});
	}

	function toastCartError(error) {
		if (!NS.Toast) return;
		var message = errorText(error && error.payload) || (error && error.message) || '';
		var generic =
			!message ||
			message === 'Could not add to cart' ||
			message === 'Request failed' ||
			message === 'Empty JSON response' ||
			message === 'Failed to fetch';
		NS.Toast.error({
			title: 'Could not add to cart',
			message: generic ? 'Please try again.' : message,
		});
	}

	function fetchCart(broadcast) {
		return fetch(getCartUrl('cart.js'), {
			headers: { Accept: 'application/json' },
			credentials: 'same-origin',
		})
			.then(readJson)
			.then(function (cart) {
				syncFromCart(cart, !!broadcast);
				return cart;
			});
	}

	function fetchCartQuiet(broadcast) {
		return fetchCart(broadcast).catch(function () {
			return null;
		});
	}

	function syncFromCart(cart, broadcast) {
		if (isFullCart(cart) && !broadcast) {
			rememberFromCart(cart, false);
		}
		cartByVariant = {};
		(cart.items || []).forEach(function (item) {
			cartByVariant[item.variant_id] = {
				quantity: item.quantity,
				key: item.key,
			};
		});
		updateAllCards();
		if (broadcast) {
			broadcastCartEvent(cart);
		}
	}

	function getQtyForVariant(variantId) {
		var entry = cartByVariant[parseInt(variantId, 10)];
		return entry ? entry.quantity : 0;
	}

	/**
	 * What the card should show: the pending quantity while one is queued or in
	 * flight, otherwise the confirmed cart quantity. Reading this (rather than the
	 * cart) is what lets +/- accumulate across taps before anything is sent.
	 */
	function displayQty(variantId) {
		var id = String(parseInt(variantId, 10));
		return id in pendingQty ? pendingQty[id] : getQtyForVariant(variantId);
	}

	function getLineKeyForVariant(variantId) {
		var entry = cartByVariant[parseInt(variantId, 10)];
		return entry ? entry.key : null;
	}

	function findProductCard(card) {
		return card.closest('.product-card, .product-card--overlay');
	}

	function attrsFromCard(el) {
		if (!el || !el.dataset.variantId) return null;
		return {
			variantId: el.dataset.variantId,
			available: el.dataset.available !== 'false',
			qtyMin: el.dataset.qtyMin || 1,
			qtyIncrement: el.dataset.qtyIncrement || 1,
			qtyMax: el.dataset.qtyMax || null,
		};
	}

	function attrsFromVariant(variant) {
		if (!variant) {
			return { variantId: '', available: false, qtyMin: 1, qtyIncrement: 1, qtyMax: null };
		}
		var rule = variant.quantity_rule || {};
		return {
			variantId: variant.id,
			available: !!variant.available,
			qtyMin: rule.min || 1,
			qtyIncrement: rule.increment || 1,
			qtyMax: rule.max != null ? rule.max : null,
		};
	}

	function applyVariantAttrs(card, attrs) {
		if (!card || !attrs) return;
		if (attrs.variantId) card.dataset.variantId = String(attrs.variantId);
		card.dataset.available = attrs.available ? 'true' : 'false';
		if (attrs.qtyMin != null) card.dataset.qtyMin = String(attrs.qtyMin);
		if (attrs.qtyIncrement != null) card.dataset.qtyIncrement = String(attrs.qtyIncrement);
		if (attrs.qtyMax != null && attrs.qtyMax !== '') {
			card.dataset.qtyMax = String(attrs.qtyMax);
		} else {
			delete card.dataset.qtyMax;
		}

		var disabled = !attrs.available;
		var controls = card.querySelector('.ns-custom-card-qty__controls');
		if (controls) {
			controls.classList.toggle('ns-custom-card-qty__controls--disabled', disabled);
		}
		card.querySelectorAll('button, input').forEach(function (el) {
			el.disabled = disabled;
		});
		updateCard(card);
	}

	function productQtyCardsForEvent(event) {
		var data = (event && event.data) || {};
		var sectionId = data.sectionId;
		var html = data.html;
		var productId = null;

		if (sectionId) {
			var info =
				document.querySelector('product-info[data-section="' + sectionId + '"]') ||
				document.querySelector('product-info[data-original-section="' + sectionId + '"]');
			productId = info && info.getAttribute('data-product-id');
		}
		if (!productId && html && html.querySelector) {
			var htmlInfo = html.querySelector('product-info');
			productId = htmlInfo && htmlInfo.getAttribute('data-product-id');
		}
		if (!productId && data.variant && data.variant.product_id) {
			productId = data.variant.product_id;
		}

		if (!productId) return [];

		var cards = [];
		document.querySelectorAll('[data-ns-product-qty]').forEach(function (card) {
			if (card.dataset.productId && String(card.dataset.productId) !== String(productId)) {
				return;
			}
			cards.push(card);
		});
		return cards;
	}

	function onVariantChange(event) {
		var data = (event && event.data) || {};
		var attrs = null;
		if (data.html && data.html.querySelector) {
			attrs = attrsFromCard(data.html.querySelector('[data-ns-product-qty]'));
		}
		if (!attrs) attrs = attrsFromVariant(data.variant);
		productQtyCardsForEvent(event).forEach(function (card) {
			applyVariantAttrs(card, attrs);
		});
	}

	function fitQtyInput(input) {
		if (!input) return;
		var value = input.value == null ? '' : String(input.value);
		input.style.setProperty('--ns-qty-chars', String(value.length || 1));
	}

	function updateCard(card) {
		if (!card || !card.dataset.variantId) return;
		var variantId = parseInt(card.dataset.variantId, 10);
		var qty = displayQty(variantId);
		var input = card.querySelector('[data-ns-qty-input]');
		var productCard = findProductCard(card);

		if (input && document.activeElement !== input) {
			input.value = qty;
		}
		fitQtyInput(input);

		var badge = productCard ? productCard.querySelector('[data-ns-qty-badge]') : null;
		if (badge) {
			var countEl = badge.querySelector('[data-ns-qty-badge-count]');
			if (countEl) countEl.textContent = qty;
			badge.classList.toggle('hidden', qty === 0);
			badge.hidden = qty === 0;
		}

		// "In your cart" confirmation, shown alongside the control by whichever
		// host opted into it (the scan popup). Follows the optimistic quantity.
		var host = card.closest('[data-ns-qty-host]');
		var added = host ? host.querySelector('[data-ns-qty-added]') : null;
		if (added) added.hidden = qty === 0;
	}

	function updateAllCards() {
		document.querySelectorAll('[data-ns-card-qty]').forEach(updateCard);
	}

	function publishCartUpdate(cart) {
		cart = withItemCount(cart);
		if (window.FoxTheme && FoxTheme.pubsub) {
			FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
		}
		if (NS.Pricing && typeof NS.Pricing.recalculateAll === 'function') {
			NS.Pricing.recalculateAll();
		}
	}

	function hasCartUi() {
		return !!(document.querySelector('cart-drawer') || document.querySelector('main-cart'));
	}

	function refreshCartDrawer() {
		if (!document.querySelector('cart-drawer')) return;
		document.dispatchEvent(
			new CustomEvent('cart:refresh', {
				bubbles: true,
			}),
		);
	}

	function refreshMainCart() {
		var mainCart = document.querySelector('main-cart');
		if (!mainCart || !window.FoxTheme) return;
		var sectionId = FoxTheme.utils.getSectionId(mainCart);
		var cartPageUrl = (FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
		var joiner = cartPageUrl.indexOf('?') >= 0 ? '&' : '?';
		Promise.all([
			fetch(cartPageUrl + joiner + 'section_id=' + encodeURIComponent(sectionId)).then(function (r) {
				if (!r.ok) throw new Error('Cart page fetch failed');
				return r.text();
			}),
			fetch(getCartUrl('cart.js'), {
				headers: { Accept: 'application/json' },
				credentials: 'same-origin',
			}).then(readJson),
		])
			.then(function (results) {
				var cartData = results[1];
				cartData.sections = {};
				cartData.sections[sectionId] = results[0];
				if (FoxTheme.pubsub) {
					FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartData });
				}
			})
			.catch(function () {
				endSuppressBroadcast();
			});
	}

	var drawerMarkupStale = false;

	function isCartDrawerOpen() {
		var drawer = document.querySelector('cart-drawer');
		return !!(drawer && drawer.hasAttribute('open'));
	}

	function refreshCartUI() {
		refreshCartDrawer();
		refreshMainCart();
		drawerMarkupStale = false;
	}

	function refreshCartUIIfNeeded() {
		if (document.querySelector('main-cart')) {
			refreshMainCart();
		}
		if (!document.querySelector('cart-drawer')) {
			drawerMarkupStale = false;
			return;
		}
		if (isCartDrawerOpen()) {
			refreshCartUI();
			return;
		}
		// Drawer is closed — skip the section download and extra /cart.js.
		drawerMarkupStale = true;
	}

	function onCartDrawerShown() {
		if (!drawerMarkupStale) return;
		refreshCartUI();
	}

	function onCartUpdateEvent(cart) {
		if (suppressBroadcast) {
			if (cart && cart.sections) endSuppressBroadcast();
			return;
		}
		applyLocalCartUpdate(cart);
		if (!(cart && cart.sections)) refreshCartUIIfNeeded();
	}

	function applyRemoteCart(cart) {
		beginSuppressBroadcast();
		if (isFullCart(cart)) {
			syncFromCart(cart, false);
		}
		publishCartUpdate(cart);
		refreshCartUIIfNeeded();
		if (!hasCartUi()) {
			endSuppressBroadcast();
		}
	}

	var qtyUpdateQueued = null;
	var qtyUpdateWaiters = [];
	var qtyUpdateInflight = null;

	function normalizeDesiredQty(quantity) {
		return Math.max(0, Math.floor(Number(quantity) || 0));
	}

	function mergeDesiredMap(into, patch) {
		var next = Object.assign({}, into || {});
		Object.keys(patch || {}).forEach(function (variantId) {
			next[String(variantId)] = normalizeDesiredQty(patch[variantId]);
		});
		return next;
	}

	function cartFingerprintLines(cart) {
		return ((cart && cart.items) || [])
			.map(function (item) {
				return String(item.variant_id || '') + ':' + String(item.key || '') + ':' + String(item.quantity || 0);
			})
			.sort()
			.join('|');
	}

	function settleQtyWaiters(waiters, cart, error) {
		(waiters || []).forEach(function (waiter) {
			if (error) waiter.reject(error);
			else waiter.resolve(cart);
		});
	}

	function postCartUpdate(updatesByLineKey) {
		return fetch(getCartUrl('cart/update.js'), {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ updates: updatesByLineKey }),
		}).then(readJson).then(function (cart) {
			if (!isFullCart(cart)) throw new Error('Cart update returned an invalid cart payload');
			return withItemCount(cart);
		});
	}

	function postCartAdd(toAdd, cart) {
		return fetch(getCartUrl('cart/add.js'), {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ items: toAdd }),
		})
			.then(readJson)
			.then(function (addPayload) {
				return mergeAddResponseIntoCart(cart, addPayload);
			});
	}

	/**
	 * Apply absolute desired quantities. One update.js and/or one add.js.
	 * Merges add.js lines into the known cart (no follow-up /cart.js).
	 */
	function mutateDesiredOnce(desiredByVariantId, cart) {
		var nextCart = withItemCount({
			items: ((cart && cart.items) || []).map(function (item) {
				return Object.assign({}, item);
			}),
			item_count: cart && cart.item_count,
		});
		var updatesByLineKey = {};
		var toAdd = [];
		var didMutate = false;

		Object.keys(desiredByVariantId || {}).forEach(function (variantId) {
			var id = parseInt(variantId, 10);
			if (!id) return;
			var qty = normalizeDesiredQty(desiredByVariantId[variantId]);
			var existing = null;
			for (var i = 0; i < nextCart.items.length; i++) {
				if (Number(nextCart.items[i].variant_id) === id) {
					existing = nextCart.items[i];
					break;
				}
			}
			if (existing) {
				if (existing.quantity !== qty) updatesByLineKey[existing.key] = qty;
			} else if (qty > 0) {
				toAdd.push({ id: id, quantity: qty });
			}
		});

		var chain = Promise.resolve(nextCart);
		if (Object.keys(updatesByLineKey).length > 0) {
			chain = chain.then(function () {
				return postCartUpdate(updatesByLineKey);
			}).then(function (updated) {
				didMutate = true;
				nextCart = updated;
				syncFromCart(nextCart, false);
				return nextCart;
			});
		}
		if (toAdd.length > 0) {
			chain = chain.then(function (current) {
				return postCartAdd(toAdd, current);
			}).then(function (updated) {
				didMutate = true;
				nextCart = updated;
				syncFromCart(nextCart, false);
				return nextCart;
			});
		}

		return chain.then(function (finalCart) {
			return { cart: finalCart, didMutate: didMutate };
		});
	}

	function runQuantityUpdateQueue(initialDesired, cartSeed) {
		var nextCart = isFullCart(cartSeed) ? withItemCount(cartSeed) : knownCartFromSnapshot();
		var desired = mergeDesiredMap({}, initialDesired);
		var didMutateAny = false;
		var pendingWaiters = [];

		function step() {
			if (!Object.keys(desired).length) {
				if (didMutateAny) {
					syncFromCart(nextCart, true);
					publishCartUpdate(nextCart);
				}
				settleQtyWaiters(pendingWaiters.concat(qtyUpdateWaiters), nextCart);
				qtyUpdateWaiters = [];
				return nextCart;
			}

			pendingWaiters = pendingWaiters.concat(qtyUpdateWaiters);
			qtyUpdateWaiters = [];
			qtyUpdateQueued = null;
			var beforeFp = cartFingerprintLines(nextCart);

			return mutateDesiredOnce(desired, nextCart)
				.then(function (result) {
					nextCart = result.cart;
					if (result.didMutate) didMutateAny = true;

					pendingWaiters = pendingWaiters.concat(qtyUpdateWaiters);
					qtyUpdateWaiters = [];

					if (qtyUpdateQueued && Object.keys(qtyUpdateQueued).length) {
						desired = qtyUpdateQueued;
						qtyUpdateQueued = null;
						return step();
					}

					if (didMutateAny) {
						syncFromCart(nextCart, true);
						publishCartUpdate(nextCart);
					}
					settleQtyWaiters(pendingWaiters, nextCart);
					return nextCart;
				})
				.catch(function (error) {
					if (cartFingerprintLines(knownCartFromSnapshot()) !== beforeFp) {
						nextCart = knownCartFromSnapshot();
						didMutateAny = true;
					}
					pendingWaiters = pendingWaiters.concat(qtyUpdateWaiters);
					qtyUpdateWaiters = [];
					qtyUpdateQueued = null;
					if (didMutateAny) {
						syncFromCart(nextCart, true);
						publishCartUpdate(nextCart);
					}
					settleQtyWaiters(pendingWaiters, nextCart, error);
					throw error;
				});
		}

		return step().finally(function () {
			qtyUpdateInflight = null;
		});
	}

	/**
	 * Absolute quantities by variant id. Coalesces concurrent callers into one
	 * follow-up mutation. Publishes cart-update (header <cart-count>) without /cart.js
	 * after add.js by merging returned lines into the known cart.
	 *
	 * Used by product cards and storefront embeds (order guide).
	 */
	function applyQuantityUpdates(desiredByVariantId, cartSeed) {
		var desired = mergeDesiredMap({}, desiredByVariantId);
		if (!Object.keys(desired).length) {
			return Promise.resolve(isFullCart(cartSeed) ? withItemCount(cartSeed) : knownCartFromSnapshot());
		}

		if (qtyUpdateInflight) {
			qtyUpdateQueued = mergeDesiredMap(qtyUpdateQueued || {}, desired);
			return new Promise(function (resolve, reject) {
				qtyUpdateWaiters.push({ resolve: resolve, reject: reject });
			});
		}

		if (isFullCart(cartSeed)) {
			syncFromCart(withItemCount(cartSeed), false);
		}

		var run = runQuantityUpdateQueue(desired, cartSeed || knownCartFromSnapshot());
		qtyUpdateInflight = run;
		return run;
	}

	function setQuantity(variantId, quantity) {
		var qty = normalizeDesiredQty(quantity);
		var desired = {};
		desired[String(variantId)] = qty;
		return applyQuantityUpdates(desired);
	}

	function getCardFromEvent(target) {
		return target.closest('[data-ns-card-qty]');
	}

	function variantCards(id) {
		return document.querySelectorAll('[data-ns-card-qty][data-variant-id="' + id + '"]');
	}

	function setVariantLoading(id, loading) {
		variantCards(id).forEach(function (card) {
			card.classList.toggle('ns-custom-card-qty--loading', loading);
		});
	}

	/**
	 * Write the quantity even into a focused input. `updateCard` leaves a focused
	 * input alone so typing is never clobbered, but a +/- tap still has to show:
	 * Safari leaves focus on the input when a sibling button is clicked.
	 */
	function writeVariantInputs(id, qty) {
		variantCards(id).forEach(function (card) {
			var input = card.querySelector('[data-ns-qty-input]');
			if (input) {
				input.value = qty;
				fitQtyInput(input);
			}
		});
	}

	/**
	 * Send the pending quantity for one variant. Only one request per variant is
	 * ever in flight; taps that land while it runs are picked up by the re-flush
	 * on completion, so the cart always converges on the last value asked for.
	 */
	function flushQuantity(id) {
		if (inflight[id] || !(id in pendingQty)) return;

		var target = pendingQty[id];
		inflight[id] = true;
		clearTimeout(pendingTimers[id]);
		setVariantLoading(id, true);

		setQuantity(parseInt(id, 10), target)
			.then(function () {
				// Left alone if a newer tap already replaced it.
				if (pendingQty[id] === target) delete pendingQty[id];
			})
			.catch(function (error) {
				// Shopify still puts the available quantity in the cart when it
				// rejects the rest ("only X items could be added"). Re-read the
				// cart so the card matches what was actually added.
				toastCartError(error);
				return fetchCartQuiet(true).then(function (cart) {
					if (pendingQty[id] === target) {
						delete pendingQty[id];
						writeVariantInputs(id, getQtyForVariant(id));
					}
					if (cart) publishCartUpdate(cart);
				});
			})
			.finally(function () {
				inflight[id] = false;
				setVariantLoading(id, false);
				if (id in pendingQty) flushQuantity(id);
				else updateAllCards();
			});
	}

	/**
	 * Show `quantity` immediately and commit it once the shopper stops adjusting.
	 * Pass `delay: 0` to commit right away (a committed input, say), and
	 * `fromInput: true` while the shopper is still typing into the field.
	 */
	function queueQuantity(card, quantity, delay, fromInput) {
		if (!card || card.dataset.available === 'false') return;
		var id = String(parseInt(card.dataset.variantId, 10));
		var qty = parseInt(quantity, 10);
		if (isNaN(qty) || qty < 0) qty = 0;

		pendingQty[id] = qty;
		if (!fromInput) writeVariantInputs(id, qty);
		updateAllCards();

		clearTimeout(pendingTimers[id]);
		if (delay === 0) {
			flushQuantity(id);
			return;
		}
		pendingTimers[id] = setTimeout(function () {
			flushQuantity(id);
		}, delay || CLICK_DEBOUNCE_MS);
	}

	/** Commit everything still waiting — used when the page is being hidden. */
	function flushPending() {
		Object.keys(pendingQty).forEach(flushQuantity);
	}

	function handlePlus(card) {
		if (!card || card.dataset.available === 'false') return;
		var increment = parseInt(card.dataset.qtyIncrement, 10) || 1;
		var min = parseInt(card.dataset.qtyMin, 10) || 1;
		var current = displayQty(card.dataset.variantId);
		var next = current === 0 ? min : current + increment;
		var max = card.dataset.qtyMax ? parseInt(card.dataset.qtyMax, 10) : null;
		if (max !== null && next > max) next = max;
		queueQuantity(card, next);
	}

	function handleMinus(card) {
		if (!card || card.dataset.available === 'false') return;
		var increment = parseInt(card.dataset.qtyIncrement, 10) || 1;
		var current = displayQty(card.dataset.variantId);
		var next = current <= increment ? 0 : current - increment;
		queueQuantity(card, next);
	}

	function bindEvents() {
		document.addEventListener('click', function (e) {
			var plus = e.target.closest('[data-ns-qty-plus]');
			if (plus) {
				e.preventDefault();
				e.stopPropagation();
				handlePlus(getCardFromEvent(plus));
				return;
			}
			var minus = e.target.closest('[data-ns-qty-minus]');
			if (minus) {
				e.preventDefault();
				e.stopPropagation();
				handleMinus(getCardFromEvent(minus));
			}
		});

		// A committed value (blur / Enter) goes straight through.
		document.addEventListener(
			'change',
			function (e) {
				var input = e.target.closest('[data-ns-qty-input]');
				if (!input) return;
				var card = getCardFromEvent(input);
				if (!card) return;
				queueQuantity(card, input.value, 0);
			},
			true,
		);

		document.addEventListener('input', function (e) {
			var input = e.target.closest('[data-ns-qty-input]');
			if (!input) return;
			var card = getCardFromEvent(input);
			if (!card) return;
			fitQtyInput(input);
			queueQuantity(card, input.value, TYPING_DEBOUNCE_MS, true);
		});

		window.addEventListener('storage', function (e) {
			if (e.key !== STORAGE_KEY) return;
			var payload = parseCartEvent(e.newValue);
			if (payload) {
				applyRemoteCart({
					items: payload.items,
					item_count: payload.item_count,
				});
				return;
			}
			fetchCartQuiet().then(function (cart) {
				if (!cart) return;
				applyRemoteCart(cart);
			});
		});

		if (window.FoxTheme && FoxTheme.pubsub) {
			FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
				onCartUpdateEvent(event && event.cart);
			});
			FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, onVariantChange);
		}

		document.addEventListener('cart:updated', function (e) {
			onCartUpdateEvent(e.detail && e.detail.cart);
		});

		document.addEventListener('drawer:handleAfterShow', function (e) {
			var target = e.target;
			if (!target || !target.matches || !target.matches('cart-drawer')) return;
			onCartDrawerShown();
		});

		document.addEventListener('variant:changed', function (e) {
			var variant = e.detail && e.detail.variant;
			if (!variant) return;
			onVariantChange({ data: { variant: variant } });
		});

		// The product form no longer has an ATC submit. Block implicit Enter
		// submits so a focused qty field cannot add a second line.
		document.addEventListener('submit', function (e) {
			var form = e.target;
			if (!form || !form.querySelector || !form.querySelector('[data-ns-product-qty]')) return;
			var submitter = e.submitter;
			if (submitter && (submitter.getAttribute('name') === 'add' || submitter.closest('.shopify-payment-button'))) {
				return;
			}
			e.preventDefault();
		});

		// Don't let a debounced change die with the page.
		window.addEventListener('pagehide', flushPending);

		document.addEventListener('visibilitychange', function () {
			if (document.visibilityState !== 'visible') {
				flushPending();
				return;
			}
			var prevFingerprint = cartFingerprint(lastSnapshot.items, lastSnapshot.item_count);
			fetchCartQuiet(false).then(function (cart) {
				if (!isFullCart(cart)) return;
				if (cartFingerprint(lastSnapshot.items, lastSnapshot.item_count) === prevFingerprint) return;
				publishCartUpdate(cart);
				refreshCartUIIfNeeded();
			});
		});
	}

	function observeDOM() {
		var observer = new MutationObserver(function (mutations) {
			var needsUpdate = false;
			mutations.forEach(function (mutation) {
				mutation.addedNodes.forEach(function (node) {
					if (node.nodeType !== 1) return;
					if (node.matches && node.matches('[data-ns-card-qty]')) {
						updateCard(node);
						needsUpdate = true;
					}
					if (node.querySelectorAll) {
						var cards = node.querySelectorAll('[data-ns-card-qty]');
						if (cards.length) {
							cards.forEach(updateCard);
							needsUpdate = true;
						}
					}
				});
			});
			if (needsUpdate && Object.keys(cartByVariant).length === 0) {
				fetchCartQuiet();
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
	}

	NS.CardQty = {
		CART_EVENT_KEY: STORAGE_KEY,
		fetchCart: fetchCart,
		updateAllCards: updateAllCards,
		syncFromCart: syncFromCart,
		broadcastCartEvent: broadcastCartEvent,
		parseCartEvent: parseCartEvent,
		/** Absolute qty by variant id; merges add.js into known cart; updates header count. */
		applyQuantityUpdates: applyQuantityUpdates,
		mergeAddResponseIntoCart: mergeAddResponseIntoCart,
	};

	function init() {
		bindEvents();
		observeDOM();
		fetchCartQuiet().then(updateAllCards);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
